import React, { ReactElement, useState } from 'react'

import Button from '@instabox/internal-web-ui/components/Button'
import Table, { TableRowType } from '@instabox/internal-web-ui/components/Table'
import CheckIcon from '@instabox/internal-web-ui/icons/CheckIcon'
import PlusIcon from '@instabox/internal-web-ui/icons/PlusIcon'
import { GemfuryPackage } from '@instabox/lockers-update-common/types'
import { formatISO9075 } from 'date-fns'

import fetch from '../../fetch'
import usePackages from '../../hooks/usePackages'
import PackageDetailsButton from '../components/PackageDetailsButton'
import TableActionsContainer from '../components/TableActionsContainer'

interface ToggleButtonProps {
  checked: boolean
  'data-testid': string
  onChange: (rolloutCandidate: boolean) => Promise<void>
}

interface GemfuryPackageTableView
  extends Omit<GemfuryPackage, 'updatedAt' | 'rolloutCandidate' | 'packageOfInterest'>,
    TableRowType {
  actions: JSX.Element
  updatedAt: string
  rolloutCandidate: JSX.Element
  packageOfInterest: JSX.Element
}

const ToggleButton: React.FC<ToggleButtonProps> = ({ checked, onChange, 'data-testid': dataTestId }) => {
  const [loading, setLoading] = useState<boolean>(false)
  const handleClick = async () => {
    setLoading(true)
    try {
      await onChange(!checked)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      size="sm"
      shape="outlined"
      icon={
        <>
          {!loading && checked && <CheckIcon data-testid={`${dataTestId}-checked`} />}
          {!loading && !checked && <PlusIcon data-testid={`${dataTestId}-unchecked`} />}
        </>
      }
      variant={checked ? 'success' : 'primary'}
      onClick={handleClick}
      isLoading={loading}
      data-testid={dataTestId}
    />
  )
}
function Packages(): ReactElement {
  const { packages, loading, reload } = usePackages()

  async function updatePackage(name: string, candidate: boolean, packageOfInterest: boolean): Promise<void> {
    const url = `/api/package/${name}`
    await fetch(url, {
      method: 'PUT',
      body: JSON.stringify({ rolloutCandidate: candidate, packageOfInterest }),
    })
    await reload()
  }

  const rows: GemfuryPackageTableView[] = packages.map((pkg) => ({
    ...pkg,
    rolloutCandidate: (
      <ToggleButton
        checked={pkg.rolloutCandidate}
        onChange={(candidate) => updatePackage(pkg.name, candidate, pkg.packageOfInterest)}
        data-testid={`rolloutButton-${pkg.name}`}
      />
    ),
    updatedAt: formatISO9075(new Date(pkg.updatedAt)),
    disableCheckbox: true,
    packageOfInterest: (
      <ToggleButton
        checked={pkg.packageOfInterest}
        onChange={(ofInterest) => updatePackage(pkg.name, pkg.rolloutCandidate, ofInterest)}
        data-testid={`packageOfInterestButton-${pkg.name}`}
      />
    ),
    actions: (
      <TableActionsContainer>
        <PackageDetailsButton name={pkg.name} data-testid={`detail-button-${pkg.name}`} />
      </TableActionsContainer>
    ),
  }))

  return (
    <Table
      isLoading={loading}
      headers={{
        rolloutCandidate: 'Part of Rollout',
        name: 'Name',
        version: 'Version',
        updatedAt: 'Updated At',
        packageOfInterest: 'Package of Interest',
        actions: '',
      }}
      rows={rows}
    />
  )
}

export default Packages
